import React from 'react';
import styled from 'styled-components';

import VisuallyHidden from 'components/VisuallyHidden/VisuallyHidden';
import Text from 'components/Text';
import Container from 'components/Container';
import { color } from 'styles/utils';

import AppDynamics from 'images/integrations/logo-appdynamics.svg?svgr';
import AzureDevops from 'images/integrations/logo-azure-devops.svg?svgr';
import Aws from 'images/integrations/logo-aws.svg?svgr';
import Buildkite from 'images/integrations/logo-buildkite.svg?svgr';
import Blameless from 'images/integrations/logo-blameless.svg?svgr';
import Bugsnag from 'images/integrations/logo-bugsnag.svg?svgr';
import Bitbucket from 'images/integrations/logo-bitbucket.svg?svgr';
import Circleci from 'images/integrations/logo-circleci.svg?svgr';
import Datadog from 'images/integrations/logo-datadog.svg?svgr';
import FireHydrant from 'images/integrations/logo-firehydrant.svg?svgr';
import Github from 'images/integrations/logo-github.svg?svgr';
import Gitlab from 'images/integrations/logo-gitlab.svg?svgr';
import Jenkins from 'images/integrations/logo-jenkins.svg?svgr';
import Jira from 'images/integrations/logo-jira.svg?svgr';
import LaunchDarkly from 'images/integrations/logo-launchdarkly.svg?svgr';
import Linear from 'images/integrations/logo-linear.svg?svgr';
import MicrosoftTeams from 'images/integrations/logo-microsoft-teams.svg?svgr';
import NewRelic from 'images/integrations/logo-new-relic.svg?svgr';
import Pagerduty from 'images/integrations/logo-pagerduty.svg?svgr';
import Rollbar from 'images/integrations/logo-rollbar.svg?svgr';
import Rootly from 'images/integrations/logo-rootly.svg?svgr';
import Sentry from 'images/integrations/logo-sentry.svg?svgr';
import ServiceNow from 'images/integrations/logo-servicenow.svg?svgr';
import Slack from 'images/integrations/logo-slack.svg?svgr';
import Splunk from 'images/integrations/logo-splunk.svg?svgr';
import Shortcut from 'images/integrations/logo-shortcut.svg?svgr';
import Terraform from 'images/integrations/logo-terraform.svg?svgr';
import Webhooks from 'images/integrations/logo-webhooks.svg?svgr';

const Section = styled.section`
  background-color: ${color('lightestBlue')};
  padding: 4rem 1rem;
`;

const StyledContainer = styled(Container)`
  width: 57rem;
`;

const Partners = styled.ul`
  align-items: center;
  display: grid;
  gap: 1rem;
  grid-auto-rows: 1fr;
  grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
  max-width: 100%;
`;

const Card = styled.li`
  padding: 3rem 2rem 1rem 2rem;
  background-color: ${color('white')};
  text-align: center;
  border-radius: 0.5rem;
  box-shadow: 0px 2px 10px 0px #0000000d;
  height: 100%;
  a {
    text-decoration: none;
  }
  h5 {
    margin: 2rem 0 1rem 0;
  }
`;

const Logo = styled.span`
  width: 100%;
  height: 4rem;
  max-height: 4rem;
`;

const partnerData = [
  {
    title: 'AppDynamics',
    description: 'See the impact of deploys on your AppDynamics metrics.',
    link: 'https://help.sleuth.io/integrations-1/impact-sources/metrics/appdynamics',
    logo: AppDynamics,
  },
  {
    title: 'AWS CloudWatch',
    description: 'See the impact of deploys on your AWS CloudWatch metrics.',
    link: 'https://help.sleuth.io/integrations-1/impact-sources/metrics/aws-cloudwatch',
    logo: Aws,
  },
  {
    title: 'Azure DevOps',
    description:
      'Use Azure DevOps as source control, issue tracker, or builds.',
    link: 'https://help.sleuth.io/integrations-1/code-deployment/azure-devops',
    logo: AzureDevops,
  },
  {
    title: 'Bitbucket',
    description:
      'Get insights on code reviews, PRs, builds, etc. from Bitbucket data.',
    link: 'https://help.sleuth.io/integrations-1/code-deployment/bitbucket',
    logo: Bitbucket,
  },
  {
    title: 'Blameless',
    description: 'Track Blameless incidents in Sleuth.',
    link: 'https://help.sleuth.io/integrations-1/incident-tracker-integrations/blameless',
    logo: Blameless,
  },
  {
    title: 'Bugsnag',
    description: 'See the impact of deploys on errors tracked by Bugsnag.',
    link: 'https://help.sleuth.io/integrations-1/impact-sources/errors/bugsnag',
    logo: Bugsnag,
  },
  {
    title: 'Buildkite',
    description: 'See information on Buildkite builds for every deploy.',
    link: 'https://help.sleuth.io/integrations-1/builds/buildkite',
    logo: Buildkite,
  },
  {
    title: 'CircleCI',
    description: 'See information on CircleCI builds for every deploy.',
    link: 'https://help.sleuth.io/integrations-1/builds/circleci',
    logo: Circleci,
  },
  {
    title: 'Datadog',
    description: 'See the impact of deploys on your Datadog metrics.',
    link: 'https://help.sleuth.io/integrations-1/impact-sources/metrics/datadog',
    logo: Datadog,
  },
  {
    title: 'FireHydrant',
    description: 'Track FireHydrant incidents in Sleuth.',
    link: 'https://help.sleuth.io/integrations-1/incident-tracker-integrations/firehydrant',
    logo: FireHydrant,
  },
  {
    title: 'GitHub',
    description:
      'Get insights into code reviews, PRs, builds, and more from GitHub data.',
    link: 'https://help.sleuth.io/integrations-1/code-deployment/github',
    logo: Github,
  },
  {
    title: 'Gitlab',
    description:
      'Get insights into code reviews, PRs, builds, and more from GitLab data.',
    link: 'https://help.sleuth.io/integrations-1/code-deployment/gitlab',
    logo: Gitlab,
  },
  {
    title: 'PagerDuty',
    description: 'Track PagerDuty incidents in Sleuth.',
    link: 'https://help.sleuth.io/integrations-1/incident-tracker-integrations/pagerduty',
    logo: Pagerduty,
  },
  {
    title: 'Jenkins',
    description: 'See information on Jenkins builds for every deploy.',
    link: 'https://help.sleuth.io/integrations-1/impact-sources/build-trackers/jenkins',
    logo: Jenkins,
  },
  {
    title: 'Jira',
    description: "Trace deploys to Jira issue tickets to know what's shipped.",
    link: 'https://help.sleuth.io/integrations-1/issue-trackers/jira',
    logo: Jira,
  },
  {
    title: 'LaunchDarkly',
    description:
      'Track feature flag changes and their impact on your app or service.',
    link: 'https://help.sleuth.io/integrations-1/feature-flags/launchdarkly',
    logo: LaunchDarkly,
  },
  {
    title: 'Linear',
    description: 'Trace deploys to Linear issue tickets.',
    link: 'https://help.sleuth.io/integrations-1/issue-trackers/linear',
    logo: Linear,
  },
  {
    title: 'Microsoft Teams',
    description:
      'Get notified of deploys, pull requests, environment drift and more.',
    link: 'https://help.sleuth.io/integrations-1/microsoft-teams-integration',
    logo: MicrosoftTeams,
  },
  {
    title: 'New Relic',
    description: 'See the impact of deploys on your New Relic metrics.',
    link: 'https://help.sleuth.io/integrations-1/impact-sources/metrics/newrelic',
    logo: NewRelic,
  },
  {
    title: 'Rollbar',
    description: 'See the impact of deploys on errors tracked by Rollbar.',
    link: 'https://help.sleuth.io/integrations-1/impact-sources/errors/rollbar',
    logo: Rollbar,
  },
  {
    title: 'Rootly',
    description: 'Track Rootly incidents in Sleuth.',
    link: 'https://help.sleuth.io/integrations-1/incident-tracker-integrations/rootly',
    logo: Rootly,
  },
  {
    title: 'Sentry',
    description: 'See the impact of deploys on errors tracked by Sentry.',
    link: 'https://help.sleuth.io/integrations-1/impact-sources/errors/sentry',
    logo: Sentry,
  },
  {
    title: 'ServiceNow',
    description: 'Track ServiceNow incidents in Sleuth.',
    link: 'https://help.sleuth.io/integrations-1/incident-tracker-integrations/servicenow',
    logo: ServiceNow,
  },
  {
    title: 'Shortcut',
    description:
      "Trace deploys to Shortcut issue tickets to know what's shipped.",
    link: 'https://help.sleuth.io/integrations-1/issue-trackers/shortcut',
    logo: Shortcut,
  },
  {
    title: 'Slack',
    description: 'Get notified of deploys, approve deploys with 👍, and more.',
    link: 'https://help.sleuth.io/integrations-1/slack',
    logo: Slack,
  },
  {
    title: 'Splunk',
    description:
      'See the impact of deploys on your Splunk observability metrics.',
    link: 'https://help.sleuth.io/integrations-1/impact-sources/metrics/signalfx',
    logo: Splunk,
  },
  {
    title: 'Terraform',
    description: 'Automatically register a deploy in Sleuth using Terraform.',
    link: 'https://help.sleuth.io/integrations-1/code-deployment/terraform-cloud',
    logo: Terraform,
  },
  {
    title: 'Webhooks',
    description:
      'Use webhooks as an alternative to CI/CD and other integrations.',
    link: null,
    logo: Webhooks,
  },
];

export default function PartnerGrid() {
  return (
    <Section>
      <StyledContainer>
        <VisuallyHidden>Grid of partners</VisuallyHidden>
        <Partners role="list">
          {partnerData.map((component) => (
            <Card key={component.title}>
              <a
                href={component.link || undefined}
                target="_blank"
                rel="noreferrer"
              >
                <Logo aria-hidden="true" as={component.logo} />
                <Text as="h5" variant="f5" color="mediumTeal">
                  {component.title}
                </Text>
                <Text variant="smallestP" color="textTeal">
                  {component.description}
                </Text>
              </a>
            </Card>
          ))}
        </Partners>
      </StyledContainer>
    </Section>
  );
}
